import React, { useEffect, useRef } from "react";
import { Box, IconButton, Paper, Stack } from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import CustomTypography from "../../../../../../views/components/common/form/CustomTypography";
import AppButton from "../../../../../../views/components/common/buttons/AppButton";
import { useStores } from "../../../../use-stores";
import { useNotification } from "../../../../../../utils/NotificationsContext";
import { observer } from "mobx-react-lite";

/**
 * Right-rail copy for the Mockups step (generator lives in the center preview).
 */
const MockupsSettingsPanel: React.FC = () => {
  const { swagStoreStore } = useStores();
  const { showError } = useNotification();
  const uploadInputRef = useRef<HTMLInputElement>(null);

  const storeId = swagStoreStore.storeId;
  const customerId = swagStoreStore.settings.storeDetails.customer_id;

  useEffect(() => {
    void swagStoreStore.loadUploadedMockupLogos().catch((e) => {
      showError(
        "Failed to load logos",
        e?.response?.data?.message || "Failed to load logos",
      );
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storeId, customerId]);

  const handleUploadLogoToLibrary = (file: File) => {
    if (!customerId) return;
    swagStoreStore.uploadMockupLogo(file).catch((e) => {
      showError(
        "Failed to save logo",
        e?.response?.data?.message || e?.message || "Failed to save logo",
      );
    });
  };

  const handleDeleteUploadedLogo = (logoId: number) => {
    if (!customerId) return;
    swagStoreStore.deleteMockupLogo(logoId).catch((error) => {
      showError(
        "Error deleting uploaded logo",
        error?.response?.data?.message || "Failed to delete uploaded logo",
      );
    });
  };

  return (
    <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2, height: 520, display: "flex", flexDirection: "column" }}>
      <CustomTypography variant="caption" fontWeight={700} sx={{ display: "block", mb: 1 }}>
        Uploaded logos
      </CustomTypography>

      <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto", pr: 0.5 }}>
        {swagStoreStore.loadingUploadedMockupLogos ? (
          <CustomTypography variant="caption" color="text.secondary">
            Loading logos...
          </CustomTypography>
        ) : swagStoreStore.uploadedMockupLogos.length === 0 ? (
          <Box
            sx={{
              height: "100%",
              minHeight: 220,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              textAlign: "center",
              color: "text.secondary",
              px: 2,
            }}
          >
            <ImageOutlinedIcon sx={{ fontSize: 36, mb: 1, opacity: 0.7 }} />
            <CustomTypography variant="caption" fontWeight={600} sx={{ display: "block", mb: 0.5 }}>
              No logos uploaded yet
            </CustomTypography>
            <CustomTypography variant="caption" color="text.secondary" sx={{ display: "block" }}>
              Upload a logo to use it in mockup generation.
            </CustomTypography>
          </Box>
        ) : (
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(92px, 1fr))",
              gap: 1,
            }}
          >
            {swagStoreStore.uploadedMockupLogos.map((logo) => (
              <Box
                key={logo.id}
                sx={{
                  position: "relative",
                  height: 92,
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 2,
                  p: 1,
                  bgcolor: "grey.50",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  overflow: "hidden",
                  "& .logo-card-actions": { opacity: 0 },
                  "&:hover .logo-card-actions": { opacity: 1 },
                }}
              >
                <Box
                  component="img"
                  src={logo.logo_url}
                  alt="Uploaded logo"
                  sx={{ width: "100%", height: "100%", objectFit: "contain", display: "block" }}
                />
                <Stack
                  className="logo-card-actions"
                  direction="row"
                  spacing={0.5}
                  sx={{
                    position: "absolute",
                    inset: 0,
                    transition: "opacity 120ms ease",
                    bgcolor: "rgba(0, 0, 0, 0.3)",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <IconButton
                    size="small"
                    aria-label="Delete uploaded logo"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteUploadedLogo(logo.id);
                    }}
                    sx={{
                      bgcolor: "background.paper",
                      "&:hover": { bgcolor: "grey.100" },
                    }}
                  >
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </Stack>
              </Box>
            ))}
          </Box>
        )}
      </Box>

      <AppButton
        label="Upload logo"
        startIcon={<CloudUploadIcon />}
        onClick={() => uploadInputRef.current?.click()}
        customStyles={{ width: "100%", marginTop: "16px" }}
      />
      <input
        ref={uploadInputRef}
        type="file"
        accept=".png,.jpg,.jpeg,.webp,.svg"
        hidden
        onChange={(e) => {
          const file = e.target.files?.[0] ?? null;
          if (file) {
            handleUploadLogoToLibrary(file);
          }
          e.currentTarget.value = "";
        }}
      />
    </Paper>
  );
};

export default observer(MockupsSettingsPanel);
